import { Stack, Typography } from "@mui/material";
import ConfirmationDialog from "components/UI/AcxConfirmationDialog";
import React, { useState } from "react";
import { useStore } from "utils/useStore";
import { ChainSourceStore } from "../../Stores/ChainSourceStore";

/**
 * Removing a parent takes its outputs away from this model, so any prompt still
 * referencing one has to give it up too. That is destructive to text someone
 * wrote, so it is confirmed first -- and only when there is something to lose.
 */
export function useRemoveParentConfirmation() {
    const store = useStore(ChainSourceStore);

    const [pendingParentId, setPendingParentId] = useState<string>();

    const pendingParent = pendingParentId
        ? store.getParent(pendingParentId)
        : undefined;
    const references = pendingParentId
        ? store.getPromptReferences(pendingParentId)
        : [];
    const stepConditions = pendingParentId
        ? store.getStepConditionReferences(pendingParentId)
        : [];

    function requestRemove(parentId: string) {
        const isInUse =
            store.getPromptReferences(parentId).length > 0 ||
            store.getStepConditionReferences(parentId).length > 0;

        if (!isInUse) {
            store.removeParent(parentId);
            return;
        }

        setPendingParentId(parentId);
    }

    const confirmationDialog = (
        <ConfirmationDialog
            isOpen={!!pendingParentId}
            title="Remove this parent model?"
            subTitle={`${
                pendingParent?.name ?? "This model"
            } is still in use in this model's configuration.`}
            confirmButtonText="Remove and update configuration"
            cancelButtonText="Cancel"
            onClose={() => setPendingParentId(undefined)}
            onCancel={() => setPendingParentId(undefined)}
            onConfirm={() => {
                if (pendingParentId) store.removeParent(pendingParentId);
                setPendingParentId(undefined);
            }}
            content={
                <Stack direction="column" rowGap={1.5}>
                    <Typography color="gray.600">
                        Removing it takes its outputs away from this model, so
                        everything below is deleted with it.
                    </Typography>
                    {references.length > 0 && (
                        <Stack direction="column" rowGap={0.5}>
                            <Typography color="gray.700" fontWeight="bold">
                                Prompt references
                            </Typography>
                            {references.map((reference) => (
                                <Typography
                                    key={reference.stepId}
                                    color="gray.600"
                                >
                                    <strong>
                                        {reference.stepName || reference.stepId}
                                    </strong>
                                    {": "}
                                    {reference.variables.join(", ")}
                                </Typography>
                            ))}
                        </Stack>
                    )}
                    {stepConditions.length > 0 && (
                        <Stack direction="column" rowGap={0.5}>
                            <Typography color="gray.700" fontWeight="bold">
                                Step action conditions
                            </Typography>
                            {stepConditions.map((reference) => (
                                <Typography
                                    key={reference.stepId}
                                    color="gray.600"
                                >
                                    <strong>
                                        {reference.stepName || reference.stepId}
                                    </strong>
                                    {": "}
                                    {reference.variables.join(", ")}
                                    {reference.losesWholeAction &&
                                        ". The action itself is removed, since nothing would be left to gate it."}
                                </Typography>
                            ))}
                        </Stack>
                    )}
                    {references.length > 0 && (
                        <Typography color="gray.600">
                            Wording around a removed prompt reference stays as
                            written, so those prompts are worth a read
                            afterwards.
                        </Typography>
                    )}
                </Stack>
            }
        />
    );

    return { requestRemove, confirmationDialog };
}
